import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// The notification feed the desktop app reads — mirrors
// backend/src/routes/notifications.ts.
//
//   GET /api/notifications/feed?after=<id>&wait=<seconds>
//
// A long-poll: answers at once when there are events after the cursor,
// otherwise waits up to `wait` seconds for one. No cursor (null) returns no
// history, only where to start.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

export interface FeedEvent {
  id: number;
  category: 'tasks' | 'chat' | 'voice' | 'meetings' | 'alerts' | 'leaves';
  title: string;
  body: string;
  /** In-app path to open on click. */
  url: string;
  /** Collapse key. */
  tag: string;
  /** Backend PushSound, or null. */
  sound: string | null;
  createdAt: string;
}

export interface FeedPage {
  events: FeedEvent[];
  /** Send back as `after` on the next request. */
  cursor: number;
}

/** The longest the server holds a request (MAX_FEED_WAIT_SECONDS). */
export const FEED_WAIT_SECONDS = 25;

export async function pollNotificationFeed(
  after: number | null,
  wait: number,
  signal: AbortSignal,
): Promise<FeedPage> {
  const token = useAuthStore.getState().accessToken;
  const query = after === null ? '' : `after=${after}&wait=${wait}`;
  const res = await apiFetch<Envelope<FeedPage>>(
    `/api/notifications/feed${query ? `?${query}` : ''}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal },
  );
  return res.data;
}
